import type { FC } from 'react'
import { useEffect, useState } from 'react'

import type { MessageType } from '@skytech/ui'
import { InfoBox, MessagePopup } from '@skytech/ui'
import { StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation } from '@tanstack/react-query'
import { Form, FormikProvider, useFormik } from 'formik'
import { useSearchParams } from 'react-router-dom'
import * as yup from 'yup'

import { Translation } from '@/app/translations/Translation'
import PasswordView from '@/modules/login/PasswordView'
import { generateRandomPassword } from '@/util/misc'

import adminResources from '../admin/resources'

import type { PasswordConditions } from './helpers'
import {
  containsNumber,
  containsSpecialCharacter,
  containsUpperCase,
  containsLowerCase,
  evaluatePasswordConditions,
  isValidLongPass,
  isValidShortPass,
  isAtLeastLength,
} from './helpers'

const initialValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}
const createPasswordTest = (testFunc: (value: string) => boolean) =>
  function (this: yup.TestContext, value = ''): boolean {
    if (isAtLeastLength(value, 9)) {
      return true
    }
    return testFunc(value)
  }

const passwordLengthTest = function (this: yup.TestContext, value = ''): boolean {
  const conditions = evaluatePasswordConditions(value)
  if (isAtLeastLength(value, 8) && !isValidShortPass(conditions) && !isValidLongPass(conditions)) {
    return false
  }
  return true
}

const lowercaseTest = createPasswordTest(containsLowerCase)
const uppercaseTest = createPasswordTest(containsUpperCase)
const numberTest = createPasswordTest(containsNumber)
const specialCharTest = createPasswordTest(containsSpecialCharacter)

const validationSchema = yup.object({
  currentPassword: yup.string().required('This field is mandatory'),
  newPassword: yup
    .string()
    .test('lowercase', 'Must contain a lowercase letter', lowercaseTest)
    .test('uppercase', 'Must contain an uppercase letter', uppercaseTest)
    .test('number', 'Must contain a number', numberTest)
    .test('specialChar', 'Must contain a special character', specialCharTest)
    .test(
      'length8or16',
      'Must be at least 8 characters with the above requirements or at least 16 characters without',
      passwordLengthTest
    )
    .required('This field is mandatory'),
  confirmPassword: yup
    .string()
    .required('Please retype password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
})

type AccountSettingsFormProps = {
  onPasswordConditionsChange: (data: {
    conditions: PasswordConditions
    newPassword: string
  }) => void
}
export const AccountSettingsForm: FC<AccountSettingsFormProps> = ({
  onPasswordConditionsChange,
}) => {
  const [responseMessage, setResponseMessage] = useState('')
  const [title, setTitle] = useState('')
  const [type, setType] = useState<MessageType | undefined>('success')
  const [randomPassword, setRandomPassword] = useState('')
  const [searchParams] = useSearchParams()
  const isResetMissing = searchParams.get('missing-reset') === 'true' ? true : false

  const handleGeneratePassword = () => {
    const password = generateRandomPassword()
    setRandomPassword(password)
    formik.setFieldValue('newPassword', password)
    formik.setFieldValue('confirmPassword', password)
    setType('warning')
    setTitle('New Password Generated')
    setResponseMessage('The new password is:')
  }

  const onClose = () => {
    setResponseMessage('')
    setTitle('')
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: formValues => mutate(formValues),
  })

  const newPassword = formik.values.newPassword

  useEffect(() => {
    const newConditions = evaluatePasswordConditions(newPassword)
    onPasswordConditionsChange({
      conditions: newConditions,
      newPassword: newPassword,
    })
  }, [newPassword, onPasswordConditionsChange])

  const { mutate, isLoading } = useMutation(adminResources.changePassword, {
    onSuccess() {
      setType('success')
      setTitle('Success')
      setResponseMessage('password reset successfully')
      formik.resetForm()
    },
    onError(error: { text: string; response: Response; json: Object }) {
      setTitle('Error')
      setType('error')
      if (error.response.status === 401) {
        setResponseMessage('Invalid password, current password is incorrect')
      } else {
        setResponseMessage(error.response.statusText)
      }
    },
  })

  return (
    <div className="mobile:w-1/2 w-full md:w-1/3">
      {isResetMissing && (
        <InfoBox emphasis="warning" marginBottom={4}>
          <Translation id="You must reset your password before continuing to use the app!" />
        </InfoBox>
      )}
      <FormikProvider value={formik}>
        <Form>
          <PasswordView
            name="currentPassword"
            label={
              <>
                <Translation id="Current Password" />*
              </>
            }
          />
          <PasswordView
            name="newPassword"
            label={
              <>
                <Translation id="New Password" />*
              </>
            }
          />
          <PasswordView
            name="confirmPassword"
            label={
              <>
                <Translation id="Confirm Password" />*
              </>
            }
          />
          <footer className="flex flex-col justify-center gap-2 sm:flex-row sm:justify-end md:gap-4">
            <Button onClick={handleGeneratePassword} disabled={isLoading} className="mx-0">
              <Translation id="Send welcome mail" />
            </Button>
            <Button
              variant="primary"
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
              className="mx-0"
            >
              <Translation id="Save" />
            </Button>
          </footer>
        </Form>
      </FormikProvider>
      <MessagePopup
        title={<Translation id={title} />}
        type={type}
        message={
          <StyledText colorMode="gray">
            {responseMessage}{' '}
            {randomPassword && type === 'warning' && <strong>{randomPassword}</strong>}
          </StyledText>
        }
        isOpen={Boolean(responseMessage)}
        onClose={onClose}
      />
    </div>
  )
}
